import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { getEditorDataAttributeProps } from '../utils/EditorDataAttributeHelpers';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { getPropertyDisplayLabel, getChildValue } from 'components/property-editors/utils/ProviderNodeUtils';
import { TextField } from 'components/inputs/TextField';
import { Label } from 'components/inputs/Label';
import { IEditorProps } from '../Editor';
import { debounce } from 'debounce';
import { getShortcutString, getShortcutFromKeyCode } from './ShortcutUtils';
import { newShortcutInfo, ShortcutInfo } from '@decisions/server/DecisionsFramework/Design/ShortcutInfo';
// import './ShortcutEditor.less'

interface IShortcutEditorProps extends IEditorProps {}

const getClassName = () => classNames('dpc-shortcut-editor');

const SHORTCUT_DEBOUNCE_INTERVAL = 300;

/**
 * Create a keyboard shortcut.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ShortcutEditor({ child, path, hideInputLabel, onChange }: IShortcutEditorProps) {
  const dataAttrs = getEditorDataAttributeProps(child, path);
  const validation = getEditorValidationSummary(child);
  const debounced = useMemo(() => debounce(onChange, SHORTCUT_DEBOUNCE_INTERVAL), [onChange]);
  const [shortcutValue, setShortcutValue] = useState(getChildValue(child) as ShortcutInfo);
  const ignoreChange = useCallback(() => {}, []);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const value = newShortcutInfo({
      Alt: e.altKey,
      Control: e.ctrlKey,
      Shift: e.shiftKey,
      KeyName: getShortcutFromKeyCode(e),
    });
    setShortcutValue(value);
  }, []);

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      e.preventDefault();
      debounced.clear();
      debounced(shortcutValue, path);
    },
    [debounced, path, shortcutValue]
  );

  const field = (
    <TextField
      value={getShortcutString(shortcutValue)}
      onChange={ignoreChange}
      placeholder="[Not Set]"
      type="text"
      className={hideInputLabel ? getClassName() : undefined}
      validation={validation}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    />
  );

  return hideInputLabel ? (
    <>{field}</>
  ) : (
    <Label label={getPropertyDisplayLabel(child)} className={getClassName()} validation={validation} {...dataAttrs}>
      {field}
    </Label>
  );
}
